import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
  FiArchive,
  FiRepeat,
  FiGlobe,
  FiShield,
  FiZap,
  FiCalendar,
  FiX,
} from "react-icons/fi";
import { FaCrown } from "react-icons/fa";
import MarqueeText from "../common/MarqueeText";
import { compareGrades } from "../../utils/gradeSort";

const GeneratorBenchmarkTable = ({
  generators,
  benchmarkSets,
  matrixData,
  generatorStats,
  previousMatrixData = null, // For trend indicators
  latestInceptVersion = null, // Latest InceptBench version for older-version indicator
  latestDatasetVersion = null, // Latest Dataset version for older-version indicator
  latestCurriculumVersion = null, // Latest Curriculum version for older-version indicator
  showOlderDataIndicator = true, // Whether to show the "Older data" clock icon (hidden in best mode)
  curriculum = "", // Curriculum name for grade/unit label
}) => {
  const gradeLabel = curriculum?.toLowerCase() === "ap" ? "Unit" : "Grade";
  const navigate = useNavigate();
  const [showEmptyGenerators, setShowEmptyGenerators] = useState(false);
  const [selectedGeneratorId, setSelectedGeneratorId] = useState(null);
  const popoverRef = useRef(null);

  const selectedGenerator = useMemo(
    () => generators.find((g) => g.id === selectedGeneratorId) || null,
    [generators, selectedGeneratorId]
  );

  const closePopover = useCallback(() => setSelectedGeneratorId(null), []);

  useEffect(() => {
    if (!selectedGeneratorId) {
      return;
    }
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        closePopover();
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        closePopover();
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [selectedGeneratorId, closePopover]);

  const formatAuthType = (type) => {
    const labels = {
      none: "None",
      bearer: "Bearer Token",
      "x-api-key": "API Key",
    };
    return labels[type] || type;
  };

  const formatCreatedDate = (dateStr) => {
    if (!dateStr) {
      return "N/A";
    }
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Get heatmap class based on pass rate
  // Color thresholds: <90% = orange, 90-98% = yellow, 99%+ = green
  const getHeatmapClass = (passRate) => {
    if (passRate === null || passRate === undefined) {
      return "bg-gray-100 text-gray-400";
    }
    const floored = Math.floor(passRate);
    if (floored >= 99) {
      return "bg-green-200 text-green-800";
    }
    if (floored >= 90) {
      return "bg-yellow-200 text-yellow-800";
    }
    return "bg-orange-200 text-orange-800";
  };

  // Get trend indicator based on previous data
  const getTrendIndicator = (genId, bmKey, currentPassRate) => {
    if (!previousMatrixData || !previousMatrixData[genId]?.[bmKey]) {
      return null;
    }
    const previousPassRate = previousMatrixData[genId][bmKey].displayPassRate;
    const diff = currentPassRate - previousPassRate;

    if (diff > 2) {
      return {
        icon: FiTrendingUp,
        color: "text-emerald-500",
        diff: `+${diff}`,
      };
    }
    if (diff < -2) {
      return { icon: FiTrendingDown, color: "text-red-500", diff: `${diff}` };
    }
    return { icon: FiMinus, color: "text-gray-400", diff: "0" };
  };

  // Split generators into those with data and those without
  const { generatorsWithData, generatorsWithoutData } = useMemo(() => {
    const withData = [];
    const withoutData = [];

    generators.forEach((gen) => {
      const stats = generatorStats[gen.id];
      if (stats && stats.coverageRatio > 0) {
        withData.push(gen);
      } else {
        withoutData.push(gen);
      }
    });

    return { generatorsWithData: withData, generatorsWithoutData: withoutData };
  }, [generators, generatorStats]);

  // Determine which generators to show
  const visibleGenerators = useMemo(() => {
    if (showEmptyGenerators) {
      return generators;
    }
    return generatorsWithData;
  }, [generators, generatorsWithData, showEmptyGenerators]);

  // Format date for tooltip
  const formatDate = (dateString) => {
    if (!dateString) {
      return "N/A";
    }
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Get relative time string for fallback data (e.g., "1w", "3mo", "1y")
  const getRelativeAge = (dateString) => {
    if (!dateString) {
      return "";
    }
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Only show age if older than 7 days
    if (diffDays < 7) {
      return "";
    }

    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffYears >= 1) {
      return `${diffYears}y ago`;
    }
    if (diffMonths >= 1) {
      return `${diffMonths}mo ago`;
    }
    return `${diffWeeks}w ago`;
  };

  // Sort benchmarks by grade/unit using the same labels as dataset snapshots.
  const sortedBenchmarks = useMemo(() => {
    return [...benchmarkSets].sort((a, b) => compareGrades(a.grade, b.grade));
  }, [benchmarkSets]);

  // Find best performer for each grade column (excludes static runs from crown)
  // Tiebreaking order:
  //   1. Higher passRate (primary)
  //   2. Lower avgGenerationLatency (faster = better; null loses to any real value)
  //   3. Higher aggregate score
  const getBestGeneratorForGrade = (bmKey) => {
    let bestId = null;
    let bestCell = null;

    generators.forEach((gen) => {
      const cellData = matrixData[gen.id]?.[bmKey];
      if (!cellData || cellData.isStatic) {
        return;
      }

      if (bestCell === null) {
        bestId = gen.id;
        bestCell = cellData;
        return;
      }

      const passRateDiff = cellData.passRate - bestCell.passRate;
      if (passRateDiff > 0) {
        bestId = gen.id;
        bestCell = cellData;
        return;
      }
      if (passRateDiff < 0) {
        return;
      }

      // Tiebreaker 1: lower latency wins; null loses to any real value
      const cL = cellData.avgGenerationLatency;
      const bL = bestCell.avgGenerationLatency;
      if (
        cL !== null &&
        cL !== undefined &&
        (bL === null || bL === undefined)
      ) {
        bestId = gen.id;
        bestCell = cellData;
        return;
      }
      if (
        (cL === null || cL === undefined) &&
        bL !== null &&
        bL !== undefined
      ) {
        return;
      }
      if (cL !== null && cL !== undefined && bL !== null && bL !== undefined) {
        if (cL < bL) {
          bestId = gen.id;
          bestCell = cellData;
          return;
        }
        if (cL > bL) {
          return;
        }
        // equal latency — fall through to tiebreaker 2
      }

      // Tiebreaker 2: higher aggregate score wins
      if (cellData.score > bestCell.score) {
        bestId = gen.id;
        bestCell = cellData;
      }
      // If all equal: first generator in array retains crown (stable)
    });

    return bestId;
  };

  // Precompute best generators for each grade
  const bestGeneratorsByGrade = useMemo(() => {
    const result = {};
    sortedBenchmarks.forEach((bs) => {
      const bmKey = `${bs.subject}_${bs.grade}`;
      result[bmKey] = getBestGeneratorForGrade(bmKey);
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedBenchmarks, generators, matrixData]);

  if (!generators || generators.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
        <p className="text-gray-500">
          No generators with benchmark data found.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {/* Header row */}
          <thead>
            <tr className="border-b border-gray-200">
              {/* Generator column header */}
              <th className="w-52 min-w-52 border-r border-gray-200 bg-white px-4 py-3 text-left text-sm font-medium text-gray-700">
                Generator / {gradeLabel}
              </th>
              {/* Grade columns */}
              {sortedBenchmarks.map((bs) => (
                <th
                  key={bs.grade}
                  className="min-w-16 border-r border-gray-200 bg-white px-2 py-3 text-center text-sm font-medium text-gray-700 last:border-r-0"
                  title={`${gradeLabel} ${bs.grade}`}
                >
                  {bs.grade}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Generator rows */}
            {visibleGenerators.map((generator, genIdx) => {
              // Check if ALL cells for this generator are static
              const allCellsStatic = sortedBenchmarks.every((bs) => {
                const bmKey = `${bs.subject}_${bs.grade}`;
                const cell = matrixData[generator.id]?.[bmKey];
                return !cell || cell.isStatic;
              });
              const hasAnyCells = sortedBenchmarks.some((bs) => {
                const bmKey = `${bs.subject}_${bs.grade}`;
                return !!matrixData[generator.id]?.[bmKey];
              });
              const isStaticGenerator = allCellsStatic && hasAnyCells;

              return (
                <tr
                  key={generator.id}
                  className={`${
                    genIdx < visibleGenerators.length - 1
                      ? "border-b border-gray-100"
                      : ""
                  }`}
                >
                  {/* Generator name — click to show detail card */}
                  <td className="relative w-52 min-w-52 border-r border-gray-200 bg-white px-4 py-2">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedGeneratorId(
                          selectedGeneratorId === generator.id
                            ? null
                            : generator.id
                        )
                      }
                      className="flex w-full items-center gap-1.5 text-left transition-colors hover:text-primary-600"
                    >
                      <MarqueeText
                        text={generator.name}
                        maxWidth={isStaticGenerator ? 140 : 180}
                      />
                      {isStaticGenerator && (
                        <span
                          className="shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-600"
                          title="This generator produces static/identical content"
                        >
                          STATIC
                        </span>
                      )}
                    </button>
                  </td>

                  {/* Grade cells */}
                  {sortedBenchmarks.map((bs) => {
                    const bmKey = `${bs.subject}_${bs.grade}`;
                    const cellData = matrixData[generator.id]?.[bmKey];
                    const isBestInColumn =
                      bestGeneratorsByGrade[bmKey] === generator.id;
                    const trend = cellData
                      ? getTrendIndicator(
                          generator.id,
                          bmKey,
                          cellData.displayPassRate
                        )
                      : null;

                    const ageLabel =
                      showOlderDataIndicator &&
                      cellData &&
                      cellData.isUsingFallback
                        ? getRelativeAge(cellData.runDate)
                        : "";

                    const isOlderInceptVersion =
                      latestInceptVersion &&
                      cellData?.inceptbenchVersion &&
                      cellData.inceptbenchVersion !== latestInceptVersion;
                    const isOlderDatasetVersion =
                      latestDatasetVersion &&
                      cellData?.datasetVersion &&
                      cellData.datasetVersion !== latestDatasetVersion;
                    const isOlderCurriculumVersion =
                      latestCurriculumVersion &&
                      cellData?.curriculumVersion &&
                      cellData.curriculumVersion !== latestCurriculumVersion;
                    const isOlderVersion =
                      isOlderInceptVersion ||
                      isOlderDatasetVersion ||
                      isOlderCurriculumVersion;

                    // Build multiline tooltip
                    const buildTooltip = () => {
                      if (!cellData) {
                        return "No data";
                      }
                      const lines = [];
                      const actualPassRate =
                        cellData.passRate !== null &&
                        cellData.passRate !== undefined
                          ? (cellData.passRate * 100).toFixed(2)
                          : cellData.displayPassRate;
                      lines.push(
                        `${actualPassRate}% · ${cellData.itemsEvaluated} items · ${formatDate(cellData.runDate)}${cellData.isUsingFallback ? " (older data)" : ""}`
                      );
                      if (latestInceptVersion && cellData.inceptbenchVersion) {
                        const tag =
                          cellData.inceptbenchVersion === latestInceptVersion
                            ? "LATEST"
                            : "OLD";
                        lines.push(
                          `InceptBench v${cellData.inceptbenchVersion} (${tag})`
                        );
                      }
                      if (latestDatasetVersion && cellData.datasetVersion) {
                        const tag =
                          cellData.datasetVersion === latestDatasetVersion
                            ? "LATEST"
                            : "OLD";
                        lines.push(
                          `Dataset v${cellData.datasetVersion} (${tag})`
                        );
                      }
                      if (cellData.curriculumVersion) {
                        const tag =
                          latestCurriculumVersion &&
                          cellData.curriculumVersion === latestCurriculumVersion
                            ? "LATEST"
                            : "OLD";
                        lines.push(
                          `Curriculum v${cellData.curriculumVersion} (${tag})`
                        );
                      }
                      if (
                        cellData.varietyScore !== null &&
                        cellData.varietyScore !== undefined
                      ) {
                        lines.push(
                          `Variety score: ${(cellData.varietyScore * 100).toFixed(2)}`
                        );
                      }
                      if (cellData.isStatic) {
                        lines.push("⚠ Static content detected");
                      }
                      lines.push("Click to view run details");
                      return lines.join("\n");
                    };

                    return (
                      <td
                        key={bs.grade}
                        title={buildTooltip()}
                        className={`h-14 min-w-16 border-r border-white/30 last:border-r-0 ${cellData?.isStatic ? "bg-gray-200 text-gray-500" : getHeatmapClass(cellData?.displayPassRate)} ${cellData ? "cursor-pointer transition-opacity hover:opacity-80" : ""}`}
                        onClick={() => {
                          if (cellData?.runId) {
                            navigate(`/past-runs/${cellData.runId}`);
                          }
                        }}
                      >
                        <div className="relative flex h-full flex-col items-center justify-center">
                          {cellData ? (
                            <>
                              {isBestInColumn && (
                                <FaCrown className="absolute right-1 top-1 size-3 text-amber-500" />
                              )}
                              {trend && (
                                <trend.icon
                                  className={`absolute left-1 top-1 size-3 ${trend.color}`}
                                />
                              )}
                              <span className="text-sm font-bold">
                                {cellData.displayPassRate}%
                              </span>
                              {ageLabel && (
                                <span className="flex items-center gap-0.5 text-[10px] leading-tight opacity-75">
                                  <FiClock className="size-2.5" />
                                  {ageLabel}
                                </span>
                              )}
                              {isOlderVersion && (
                                <FiArchive className="absolute bottom-0.5 right-0.5 size-2.5 text-slate-400/70" />
                              )}
                              {cellData.isStatic && (
                                <FiRepeat className="absolute bottom-0.5 left-0.5 size-2.5 text-red-400/70" />
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend and Toggle */}
      <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5 text-xs text-gray-500">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-medium text-gray-600">Pass Rate:</span>
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded bg-green-200" />
              99%+
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded bg-yellow-200" />
              90-98%
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded bg-orange-200" />
              &lt;90%
            </span>
            <span className="mx-1 text-gray-300">|</span>
            <span className="flex items-center gap-1">
              <FaCrown className="size-3 text-amber-500" />
              Top performer
            </span>
            {showOlderDataIndicator && (
              <span className="flex items-center gap-1">
                <FiClock className="size-3 text-gray-400" />
                Older data
              </span>
            )}
            <span className="flex items-center gap-1">
              <FiArchive className="size-3 text-slate-400/70" />
              Older version (Hover to see)
            </span>
            <span className="flex items-center gap-1">
              <FiRepeat className="size-3 text-red-400/70" />
              Static content
            </span>
          </div>

          {/* Toggle for generators without data */}
          {generatorsWithoutData.length > 0 && (
            <button
              onClick={() => setShowEmptyGenerators(!showEmptyGenerators)}
              className="flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-gray-700"
            >
              {showEmptyGenerators ? (
                <>
                  <FiChevronUp className="size-3" />
                  Hide {generatorsWithoutData.length} generators without data
                </>
              ) : (
                <>
                  <FiChevronDown className="size-3" />
                  Show {generatorsWithoutData.length} generators without data
                </>
              )}
            </button>
          )}
        </div>
      </div>
      {/* Centered modal for generator details */}
      {selectedGenerator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            ref={popoverRef}
            className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-900">
                {selectedGenerator.name}
              </h3>
              <button
                type="button"
                onClick={closePopover}
                className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <FiX className="size-5" />
              </button>
            </div>

            {/* Details */}
            <div className="space-y-4 px-6 py-5">
              <div className="flex items-start gap-3">
                <FiGlobe className="mt-0.5 size-5 shrink-0 text-blue-500" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Endpoint
                  </p>
                  <p
                    className="mt-0.5 break-all text-sm text-gray-800"
                    title={selectedGenerator.endpoint_url}
                  >
                    {selectedGenerator.endpoint_url}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FiShield className="mt-0.5 size-5 shrink-0 text-emerald-500" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Auth Type
                  </p>
                  <p className="mt-0.5 text-sm text-gray-800">
                    {formatAuthType(selectedGenerator.auth_type)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FiZap className="mt-0.5 size-5 shrink-0 text-amber-500" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Concurrency Limit
                  </p>
                  <p className="mt-0.5 text-sm text-gray-800">
                    {selectedGenerator.concurrency_limit}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FiCalendar className="mt-0.5 size-5 shrink-0 text-purple-500" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Created
                  </p>
                  <p className="mt-0.5 text-sm text-gray-800">
                    {formatCreatedDate(selectedGenerator.created_at)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

GeneratorBenchmarkTable.propTypes = {
  generators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      endpoint_url: PropTypes.string,
      auth_type: PropTypes.string,
      concurrency_limit: PropTypes.number,
      created_at: PropTypes.string,
    })
  ),
  benchmarkSets: PropTypes.arrayOf(
    PropTypes.shape({
      subject: PropTypes.string,
      grade: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  matrixData: PropTypes.object,
  generatorStats: PropTypes.object,
  previousMatrixData: PropTypes.object,
  latestInceptVersion: PropTypes.string,
  latestDatasetVersion: PropTypes.string,
  latestCurriculumVersion: PropTypes.string,
  showOlderDataIndicator: PropTypes.bool,
  curriculum: PropTypes.string,
};

export default GeneratorBenchmarkTable;
